/**
 * Every decision the Redis monitoring page makes, extracted so it is testable.
 *
 * admin-ui's vitest runs with `environment: 'node'` and no jsdom, so DOM tests
 * are not an option here — the page module stays dumb templating and all the
 * branching lives in this file. Same split as pages/rtp-descriptor.ts.
 */

import type {
  RedisCapacity,
  RedisHistoryPoint,
  RedisHistoryWindow,
  RedisOutageDay,
} from '@apx/admin-sdk';
import { fmtBytes, fmtBytesDelta, fmtNumber } from '../format.js';

/** 1h/24h/7d are served from the raw 60s snapshots (kept 8 days); 30d/90d/1y
 *  from the daily rollup (kept 400 days). Order is the tab order. */
export const HISTORY_WINDOWS: readonly RedisHistoryWindow[] = [
  '1h',
  '24h',
  '7d',
  '30d',
  '90d',
  '1y',
];
const DEFAULT_WINDOW: RedisHistoryWindow = '24h';

/** Read + validate `?window=` off a location.search string. */
export function windowFromSearch(search: string): RedisHistoryWindow {
  const raw = new URLSearchParams(search).get('window');
  return HISTORY_WINDOWS.includes(raw as RedisHistoryWindow)
    ? (raw as RedisHistoryWindow)
    : DEFAULT_WINDOW;
}

export type Tone = 'ok' | 'warn' | 'critical' | 'unknown';

/**
 * Server status string -> UI tone.
 *
 * The UI never re-derives the percentage thresholds: they are env-configurable
 * backend-side, and a second copy here would drift silently.
 */
export function statusTone(status: RedisCapacity['status']): Tone {
  switch (status) {
    case 'normal':
      return 'ok';
    case 'warning':
      return 'warn';
    case 'critical':
      return 'critical';
    default:
      return 'unknown';
  }
}

export function toneToPillClass(tone: Tone): string {
  if (tone === 'ok') return 'pill live';
  if (tone === 'warn' || tone === 'critical') return 'pill warn';
  return 'pill';
}

export function barFillClass(status: RedisCapacity['status']): string {
  return `rm-bar-fill rm-bar-fill--${status}`;
}

/** "n/a" when there is no percentage to show, never a bare dash. */
export function pctLabel(p: number | null | undefined): string {
  return p === null || p === undefined ? 'n/a' : `${p.toFixed(1)}%`;
}

export interface Advisory {
  level: 'warn' | 'critical';
  title: string;
  body: string;
}

/**
 * Findings worth a banner, most severe first. Empty array means all clear.
 *
 * A disconnected Redis short-circuits: with no snapshot, every other finding
 * is unknowable, and listing "no maxmemory configured" under it would be
 * asserting something we did not measure.
 */
export function advisories(h: RedisCapacity): Advisory[] {
  if (h.connection === 'down') {
    return [
      {
        level: 'critical',
        title: 'Redis unreachable',
        body:
          h.error ??
          'admin-api could not reach Redis. Capacity, growth and health are unavailable until it responds.',
      },
    ];
  }

  const out: Advisory[] = [];
  // The server already decides what is worth flagging; the UI renders those
  // findings rather than re-deriving its own set.
  for (const w of h.warnings) {
    out.push({
      // The API's severity vocabulary is warning/critical; the UI's tone
      // vocabulary is warn/critical. Map rather than widen Advisory.
      level: w.severity === 'critical' ? 'critical' : 'warn',
      title: warningTitle(w.code, h),
      body: w.message,
    });
  }

  if (h.status === 'critical') {
    out.unshift({
      level: 'critical',
      title: `Memory critical — ${pctLabel(h.memory?.usedPct ?? null)} of limit used`,
      body:
        'At the limit Redis refuses writes rather than evicting, so bets, payouts and queued ' +
        'jobs start failing. Free memory or raise the cap now.',
    });
  } else if (h.status === 'warning') {
    out.unshift({
      level: 'warn',
      title: `Memory warning — ${pctLabel(h.memory?.usedPct ?? null)} of limit used`,
      body: 'Check the growth trend below to see whether this is drift or a leak.',
    });
  }

  return out.sort((a, b) => rank(b.level) - rank(a.level));
}

function rank(level: Advisory['level']): number {
  return level === 'critical' ? 1 : 0;
}

function warningTitle(code: string, h: RedisCapacity): string {
  switch (code) {
    case 'NO_MAXMEMORY':
      return 'No memory limit configured';
    case 'EVICTION_POLICY_LOSSY':
      return `Eviction policy is ${h.memory?.policy ?? 'lossy'}`;
    case 'EVICTIONS_OBSERVED':
      return 'Redis has evicted keys';
    case 'BGSAVE_FAILING':
      return 'Last background save failed';
    case 'AOF_WRITE_FAILING':
      return 'AOF writes are failing';
    case 'HIGH_FRAGMENTATION':
      return 'High memory fragmentation';
    case 'NO_CONTAINER_LIMIT_CONFIGURED':
      return 'Container limit not configured';
    default:
      return code;
  }
}

/** Signed byte delta between two buckets, for the history table's Δ column.
 *  `coverageHint` is what to print when there is no previous bucket to
 *  subtract — a dash there would read as "zero change" rather than "nothing to
 *  compare against". */
export function growthLabel(bytes: number | null, coverageHint: string): string {
  return bytes === null ? coverageHint : fmtBytesDelta(bytes);
}

/**
 * Usage tile sub-label. Spells out the unconfigured case rather than showing a
 * dash — an uncapped Redis is the #446 root cause, not a missing reading.
 */
export function limitSubLabel(h: RedisCapacity): string {
  const limit = h.memory?.maxBytes ?? null;
  if (limit === null) return 'no limit configured';
  return `of ${fmtBytes(limit)} limit`;
}

export interface DayStateLabel {
  text: string;
  tone: Tone;
}

/**
 * The worst-state cell for a daily point.
 *
 * Collapsing a day to one row is what makes a year of history affordable, and
 * it is also what would hide an incident: a daily average sails past a spike,
 * and even a daily max cannot separate "touched critical once" from "sat at
 * critical for half the afternoon". So the cell carries the worst status AND
 * how much of the day was spent there.
 *
 * Returns null on raw windows, where the buckets are minutes wide and the
 * chart already shows this directly.
 */
export function dayStateLabel(p: RedisHistoryPoint): DayStateLabel | null {
  // `== null`, not `=== null`: on a raw window the field is null, but from an
  // API build that predates it the field is absent entirely — and a strict
  // check would fall through and label every bucket a confident "normal".
  if (p.worstStatus == null) return null;
  if (p.worstStatus === 'unknown') return { text: 'no limit set', tone: 'unknown' };

  const share = (count: number): string => {
    if (p.samples <= 0) return '';
    const pct = (count / p.samples) * 100;
    // Below 0.1% still means it happened; a rounded "0.0%" would read as
    // "never", which is the one thing it does not mean.
    return pct < 0.1 ? ' (<0.1% of day)' : ` (${pct.toFixed(1)}% of day)`;
  };

  if (p.worstStatus === 'critical') {
    return { text: `critical${share(p.samplesCritical ?? 0)}`, tone: 'critical' };
  }
  if (p.worstStatus === 'warning') {
    return { text: `warning${share(p.samplesWarn ?? 0)}`, tone: 'warn' };
  }
  return { text: 'normal', tone: 'ok' };
}

/**
 * One-line summary of days with no successful sample at all.
 *
 * These are deliberately NOT points on the chart — plotting them as zero would
 * draw a memory cliff that never happened — so without this they would be
 * invisible, which is the exact failure the daily tier had to be designed
 * around. null when there were none.
 */
export function outageSummary(
  outages: readonly RedisOutageDay[] | null | undefined,
): string | null {
  // Tolerates absence, not just emptiness. admin-ui and admin-api deploy
  // separately, so a UI running ahead of an API that predates `outages` is a
  // normal transient state — and reading `.length` off undefined there took
  // out the entire Dataset growth card rather than just the outage note.
  if (!outages || outages.length === 0) return null;
  const failures = outages.reduce((a, o) => a + o.probeFailures, 0);
  const days = outages.length === 1 ? '1 day' : `${outages.length} days`;
  return `${days} with no successful sample (${fmtNumber(failures)} failed probes) — Redis was unreachable, not idle.`;
}
